/**
 * ReferenceProgressProvider — der EINE Ort, an dem der Fortschritt der Referenz-Analyse lebt
 * (Referenz-Automatik P4.2, docs/KONZEPT-AUTOMATIK-REFERENZEN.md §3.6).
 *
 * Warum zentral: Topbar-Indikator (P4.3), Referenz-Seite und Angebots-Builder (P4.4) stellen
 * dieselbe Frage. Fragte jede Seite selbst, hätte derselbe Zustand drei Quellen und der
 * Indikator wäre je Seite anders — deshalb ein Anbieter um AppShell UND den Router-Outlet,
 * Muster wie `CopilotProvider`.
 *
 * Warum die Abfrage von selbst aufhört: Das Konzept fordert ausdrücklich „Abfrage nur solange
 * etwas läuft; ist nichts in Arbeit, ruht sie vollständig". Deshalb gibt es hier KEINEN
 * Dauer-Takt. Jede Antwort entscheidet selbst, ob eine weitere folgt: nur wenn `running` wahr
 * ist, wird die nächste Runde angesetzt. Ist nichts in Arbeit, läuft kein Timer und es geht
 * keine Anfrage mehr raus, bis ein Anlass kommt (`notifyStarted()` nach einem Upload oder ein
 * neuer Seitenaufbau).
 *
 * `running` beantwortet der Server (GET /api/reference-docs/progress, P4.1) und zählt einen
 * Fehlschlag bewusst NICHT mit — sonst käme diese Abfrage nach einem gescheiterten Dokument
 * nie mehr zur Ruhe.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetch, toApiError } from "../lib/api";

/** Zustand einer Säule, exakt wie GET /api/reference-docs/progress ihn liefert. */
export interface PillarProgress {
  uploaded: number;
  extracting: number;
  extracted: number;
  failed: number;
  /** Wartet noch etwas auf die Analyse oder steckt gerade darin? */
  running: boolean;
  lastActivityAt: string | null;
}

export interface ReferenceProgress {
  catering: PillarProgress;
  event: PillarProgress;
  running: boolean;
}

export interface ReferenceProgressContextValue {
  /** null, solange noch nie eine Antwort kam (erster Aufbau oder Fehler). */
  progress: ReferenceProgress | null;
  /** Kurzform für die Anzeige: läuft irgendwo etwas? */
  running: boolean;
  /** Eine Seite hat gerade etwas angestoßen (Upload) — ab jetzt lohnt sich Nachfragen wieder. */
  notifyStarted: () => void;
}

/**
 * Takt, solange etwas läuft. Eine Analyse dauert Minuten, nicht Sekunden — häufiger zu fragen
 * bringt keine neue Auskunft und belastet nur die Datenbank. Acht Sekunden sind kurz genug,
 * dass ein Wechsel (fertig/fehlgeschlagen) sich wie sofort anfühlt.
 */
const ABFRAGE_TAKT_MS = 8_000;

const ReferenceProgressContext = createContext<ReferenceProgressContextValue | null>(null);

export function ReferenceProgressProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<ReferenceProgress | null>(null);
  // Jede Erhöhung ist genau EINE Abfrage. Die Antwort entscheidet, ob eine weitere folgt.
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    void (async () => {
      try {
        const res = await apiFetch("/api/reference-docs/progress", { signal: controller.signal });
        if (!res.ok) throw await toApiError(res);
        const body = (await res.json()) as { data: ReferenceProgress };
        setProgress(body.data);
        if (body.data.running) timer = setTimeout(() => setRunde((r) => r + 1), ABFRAGE_TAKT_MS);
      } catch {
        // Bewusst KEIN stiller Dauer-Retry: das wäre genau der Hintergrund-Verkehr, den „ruht
        // vollständig" ausschließt. Der letzte bekannte Stand bleibt stehen; der nächste Anlass
        // (Upload oder neuer Seitenaufbau) fragt erneut.
      }
    })();
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [runde]);

  const notifyStarted = useCallback(() => setRunde((r) => r + 1), []);

  return (
    <ReferenceProgressContext.Provider value={{ progress, running: progress?.running ?? false, notifyStarted }}>
      {children}
    </ReferenceProgressContext.Provider>
  );
}

/**
 * Zugriff auf den Fortschritt. Absichtlich OHNE Fehler, wenn kein Anbieter darüber liegt:
 * Seiten, die außerhalb der AppShell gerendert werden (Login, öffentliche Seiten), sollen
 * deswegen nicht abstürzen — sie sehen einfach „nichts läuft".
 */
export function useReferenceProgress(): ReferenceProgressContextValue {
  return (
    useContext(ReferenceProgressContext) ?? {
      progress: null,
      running: false,
      notifyStarted: () => {},
    }
  );
}
